// src/pages/SalesCreateRequest.jsx
import React, { useState, useEffect } from 'react';
import { getProducts } from '../api/productApi';
import { createSalesRequest } from '../api/salesRequestApi';
import { useNavigate } from 'react-router-dom';
import masaLogo from '../Assets/MasaLogo.png';
import styles from './SalesCreateRequest.module.css';

const SalesCreateRequest = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedSortName, setSelectedSortName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [action, setAction] = useState('add');
  const [quantity, setQuantity] = useState('');
  const [submissionDate, setSubmissionDate] = useState('');

  const selectedProduct = products.find(p => p._id === selectedProductId);

  useEffect(() => {
    getProducts()
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        setErrorMessage('Failed to load products. Please try again.');
      });
  }, []);

  const handleMakeRequest = async () => {
    if (!selectedProductId || !selectedSortName || !selectedColor || !action || !quantity || !submissionDate) {
      setErrorMessage('Please fill out all fields before making a request.');
      return; 
    }

    const payload = {
      product: selectedProduct.SKU,
      sortName: selectedSortName,
      color: selectedColor,
      action,
      quantity: parseInt(quantity, 10),
      submissionDate
    };

    try {
      await createSalesRequest(payload);
      navigate('/sales-my-requests');
    } catch (error) {
      console.error('Error creating sales request:', error);
      setErrorMessage('Failed to create your request. Please try again.');
    }
  };

  const closeModal = () => {
    setErrorMessage(null);
  };

  return (
    <main className={styles.salesCreateRequest}>
      <header className={styles.header}>
        <img
          loading="lazy"
          src={masaLogo}
          className={styles.logo}
          alt="Masa Logo"
        />
        <h1 className={styles.title}>Make a New Sales Request</h1>
      </header>

      <div className={styles.explanationText}>
        <p>
          Select a product, choose its variant (sort and color), specify the action (add or remove), the quantity you need, and set a desired submission date. Once done, click <strong>Make Request</strong> to send it to the manager for review.
        </p>
      </div>

      <section className={styles.productsSection}>
        <h2 className={styles.sectionTitle}>Available Products</h2>
        <div className={styles.productGrid}>
          {products.map((product) => (
            <div
              key={product._id}
              className={`${styles.productCard} ${selectedProductId === product._id ? styles.selected : ''}`}
              onClick={() => {
                setSelectedProductId(product._id);
                setSelectedSortName('');
                setSelectedColor('');
              }}
            >
              {product.image && product.image.data && product.image.contentType ? (
                <img
                  src={`data:${product.image.contentType};base64,${btoa(
                    new Uint8Array(product.image.data.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
                  )}`}
                  alt={product.name}
                  className={styles.productImage}
                />
              ) : (
                <div className={styles.placeholderImage}>No Image</div>
              )}
              <h3 className={styles.productName}>{product.name}</h3>
              <p className={styles.productDetail}><strong>SKU:</strong> {product.SKU}</p>
              <p className={styles.productDetail}><strong>Size:</strong> {product.size}</p>
            </div>
          ))}
        </div>
      </section>

      {selectedProduct && (
        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Request Details</h2>
          <div className={styles.formRow}>
            <label>Sort:</label>
            <select value={selectedSortName} onChange={(e) => setSelectedSortName(e.target.value)}>
              <option value="">Select a sort</option>
              {selectedProduct.sorts.map((sortObj) => (
                <option key={sortObj.sortName} value={sortObj.sortName}>
                  {sortObj.sortName}
                </option>
              ))}
            </select>
          </div>
          {selectedSortName && (
            <div className={styles.formRow}>
              <label>Color:</label>
              <select value={selectedColor} onChange={(e) => setSelectedColor(e.target.value)}>
                <option value="">Select a color</option>
                {selectedProduct.sorts
                  .find(s => s.sortName === selectedSortName)
                  ?.colors.map((cObj, i) => (
                    <option key={i} value={cObj.color}>
                      {cObj.color}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className={styles.formRow}>
            <label>Action:</label>
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="add">Add</option>
              <option value="remove">Remove</option>
            </select>
          </div>

          <div className={styles.formRow}>
            <label>Quantity:</label>
            <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>

          <div className={styles.formRow}>
            <label>Submission Date:</label>
            <input type="date" value={submissionDate} onChange={(e) => setSubmissionDate(e.target.value)} />
          </div>

          <button className={styles.requestButton} onClick={handleMakeRequest}>
            Make Request
          </button>
        </section>
      )}

      {/* Error Modal */}
      {errorMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Error</h2>
            <p>{errorMessage}</p>
            <button className={styles.closeButton} onClick={closeModal}>Close</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default SalesCreateRequest;
